import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const WaitlistSignup = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Simulate API call - replace with actual waitlist API
    await new Promise(resolve => setTimeout(resolve, 1000));

    setIsSubmitted(true);
    setIsLoading(false);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background via-primary/5 to-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-primary/10 p-8">
            {/* Success State */}
            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-2">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>

              <img src="/vfloow-logo.png" alt="vfloow" className="h-12 w-auto mx-auto" />

              <div className="space-y-3">
                <h2 className="text-2xl font-bold text-gray-900">You're on the list!</h2>
                <p className="text-base text-gray-600">
                  Thank you for your interest in vfloow. We'll notify you at{' '}
                  <span className="font-medium text-gray-900">{email}</span> when we're ready to
                  welcome you aboard.
                </p>
              </div>

              <div className="pt-4 space-y-3">
                <Button
                  onClick={() => navigate('/')}
                  className="w-full bg-primary hover:bg-primary/90"
                >
                  Back to Home
                </Button>
                <button
                  onClick={() => navigate('/sign-in')}
                  className="text-sm text-primary hover:text-primary/80 font-medium"
                >
                  Already have an account? Sign in
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-primary/5 to-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-primary/10 p-8">
          {/* Header */}
          <div className="text-center space-y-4 mb-8">
            <img src="/vfloow-logo.png" alt="vfloow" className="h-12 w-auto mx-auto" />

            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-gray-900">Join the Waitlist</h2>
              <p className="text-base text-gray-600">
                We're preparing something special. Be the first to know when vfloow launches.
              </p>
            </div>
          </div>

          {/* Waitlist Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="pl-10 h-12 border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-gray-900 hover:bg-gray-800 text-white font-medium text-base"
            >
              {isLoading ? 'Joining...' : 'Join Waitlist'}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-100 text-center space-y-3">
            <p className="text-sm text-gray-600">Already have an account?</p>
            <button
              onClick={() => navigate('/sign-in')}
              className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Sign in instead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitlistSignup;
